import React, { useState, useEffect } from "react";
import { useDisponibilidades } from "./useDisponibilidades";
import styles from "./DriverAvailability.module.css";

interface Props {
  driverId: number;
  veiculoId: number;
}


interface DiaGrade {
  date: string;        // "2025-09-03"
  diaSemana: number;   // 0 = Sunday ... 6 = Saturday
  horaInicio: string;
  horaFim: string;
  aberto: boolean;
}

// Days of the week in English
const diasSemana = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DriverAvailability: React.FC<Props> = ({ driverId, veiculoId }) => {
  const { disponibilidades, create, remove } = useDisponibilidades(driverId);
  const [dias, setDias] = useState<DiaGrade[]>([]);
  const [mesAtual, setMesAtual] = useState(new Date());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const ano = mesAtual.getFullYear();
    const mes = mesAtual.getMonth();
    const ultimoDia = new Date(ano, mes + 1, 0).getDate();
    const arr: DiaGrade[] = [];

    for (let d = 1; d <= ultimoDia; d++) {
      const dateObj = new Date(ano, mes, d);
      const dateStr = dateObj.toISOString().split("T")[0];
      const disp = disponibilidades.find((dd) => dd.data === dateStr);

      arr.push({
        date: dateStr,
        diaSemana: dateObj.getDay(),
        horaInicio: disp ? disp.horaInicio : "",
        horaFim: disp ? disp.horaFim : "",
        aberto: false,
      });
    }

    setDias(arr);
  }, [disponibilidades, mesAtual]);

  const toggleDia = (index: number) => {
    setDias((prev) =>
      prev.map((d, i) => (i === index ? { ...d, aberto: !d.aberto } : d))
    );
  };

  const handleChange = (index: number, campo: "horaInicio" | "horaFim", valor: string) => {
    setDias((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [campo]: valor } : d))
    );
  };

  const handleSalvar = (dia: DiaGrade) => {
    if (!dia.horaInicio || !dia.horaFim) {
      alert("Please set the times before saving.");
      return;
    }
    create(dia.date, dia.horaInicio, dia.horaFim, veiculoId);
  };


  const handleRemover = (dia: DiaGrade) => {
    const disp = disponibilidades.find((d) => d.data === dia.date);
    if (disp) remove(disp.id);
  };

  const proximoMes = () => {
    setMesAtual(new Date(mesAtual.getFullYear(), mesAtual.getMonth() + 1, 1));
  };

  const mesAnterior = () => {
    setMesAtual(new Date(mesAtual.getFullYear(), mesAtual.getMonth() - 1, 1));
  };

  const mesAnoLabel = mesAtual.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const semanas: DiaGrade[][] = [];
  let semanaAtual: DiaGrade[] = [];
  dias.forEach((dia, idx) => {
    if (idx === 0 && dia.diaSemana > 0) {
      for (let i = 0; i < dia.diaSemana; i++) semanaAtual.push(null as any);
    }
    semanaAtual.push(dia);
    if (dia.diaSemana === 6 || idx === dias.length - 1) {
      semanas.push(semanaAtual);
      semanaAtual = [];
    }
  });

  return (
    <div className={styles.dropdownContainer}>
      <button
        onClick={() => setOpen(!open)}
        className={styles.dropdownButton}
      >
        📅 Driver Availability {open ? "▲" : "▼"}
      </button>

      {open && (
        <div className={styles.container}>
          <div className={styles.header}>
            <button onClick={mesAnterior} className={styles.navBtn}>{"<"}</button>
            <h2>{mesAnoLabel}</h2>
            <button onClick={proximoMes} className={styles.navBtn}>{">"}</button>
          </div>

          <div className={styles.gridCalendario}>
            {diasSemana.map((dia) => (
              <div key={dia} className={styles.cabecalhoDia}>
                {dia}
              </div>
            ))}

            {semanas.map((semana, sIdx) =>
              semana.map((dia, dIdx) =>
                dia ? (
                  <div key={dia.date} className={styles.diaContainer}>
                    <button
                      onClick={() => toggleDia(dias.indexOf(dia))}
                      className={`${styles.dia} ${disponibilidades.find(d => d.data === dia.date) ? styles.diaAtivo : styles.diaInativo
                        }`}
                    >
                      {new Date(dia.date).getDate()}
                    </button>

                    {dia.aberto && (
                      <div className={styles.popover}>
                        <input
                          type="time"
                          value={dia.horaInicio}
                          onChange={(e) => handleChange(dias.indexOf(dia), "horaInicio", e.target.value)}
                          className={styles.input}
                        />
                        <input
                          type="time"
                          value={dia.horaFim}
                          onChange={(e) => handleChange(dias.indexOf(dia), "horaFim", e.target.value)}
                          className={styles.input}
                        />
                        <div className={styles.popoverBtns}>
                          {disponibilidades.find(d => d.data === dia.date) ? (
                            <button
                              onClick={() => handleRemover(dia)}
                              className={styles.btnRemover}
                            >
                              Remove
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSalvar(dia)}
                              className={styles.btnSalvar}
                            >
                              Save
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div key={`empty-${sIdx}-${dIdx}`} className={styles.diaVazio}></div>
                )
              )
            )}
          </div>
          <p className={styles.info}>
            Click on a day to add or remove availability.
          </p>
        </div>
      )}
    </div>
  );
};

export default DriverAvailability;